<template>
  <div class="ism-cms-wrapper">
    <!-- Unified CMS Card -->
    <div class="ism-cms-card ism-full-height-card">
      <!-- Header Section -->
      <div class="ism-cms-header">
        <div class="ism-cms-header-left">
          <div
            class="ism-cms-icon-wrap"
            style="background: rgba(242, 101, 34, 0.1)"
          >
            <v-icon color="#F26522" size="22">mdi-cash-refund</v-icon>
          </div>
          <div>
            <div class="ism-cms-title">Refund Policy</div>
            <div class="ism-cms-subtitle">
              Manage your platform's refund policy content
            </div>
          </div>
        </div>
        <div class="ism-cms-header-actions">
          <button class="ism-btn-accent" :disabled="saving" @click="saveData">
            <v-icon size="15" color="#fff">{{
              saving ? "mdi-loading mdi-spin" : "mdi-content-save-outline"
            }}</v-icon>
            {{ saving ? "Saving..." : "Save Changes" }}
          </button>
        </div>
      </div>

      <!-- Tab Navigation (Matching Society Details Style) -->
      <div class="ism-cms-sub-header">
        <div class="ism-mode-bar">
          <button
            class="ism-mode-btn"
            :class="{ active: activeMode === 'edit' }"
            @click="activeMode = 'edit'"
          >
            Edit
          </button>
          <button
            class="ism-mode-btn"
            :class="{ active: activeMode === 'preview' }"
            @click="activeMode = 'preview'"
          >
            Preview
          </button>
        </div>
      </div>

      <!-- Scrollable Content Area -->
      <div class="ism-cms-scroll-content">
        <!-- Edit Mode -->
        <div v-show="activeMode === 'edit'" class="ism-editor-pane">
          <div v-if="loading" class="ism-editor-skeleton">
            <div
              v-for="i in 10"
              :key="i"
              class="ism-shimmer"
              :style="`height:13px;border-radius:6px;width:${
                [88, 72, 80, 55, 78, 65, 90, 40, 85, 70][i - 1]
              }%;margin-bottom:12px;`"
            ></div>
          </div>
          <div v-else class="ism-editor-wrap">
            <ckeditor
              :editor="editor"
              v-model="editorData"
              :config="editorConfig"
            ></ckeditor>
          </div>
        </div>

        <!-- Preview Mode -->
        <div v-show="activeMode === 'preview'" class="ism-preview-pane">
          <div class="ism-preview-doc">
            <div
              v-if="editorData"
              class="ism-preview-content"
              v-html="editorData"
            ></div>
            <div v-else class="ism-preview-empty">
              <v-icon size="44" color="var(--border-color2)"
                >mdi-file-document-outline</v-icon
              >
              <p>No content yet. Switch to Edit mode to add content.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default {
  name: "RefundPolicy",

  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {},
      editorData: "",
      loading: false,
      saving: false,
      activeMode: "edit",
    };
  },

  computed: {
    wordCount() {
      if (!this.editorData) return 0;
      return this.editorData
        .replace(/<[^>]*>/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    },
    currentDate() {
      return new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    getData() {
      this.loading = true;
      this.$axios
        .get("superadmin/cms?identifier=refund_policy")
        .then((res) => {
          const item = res.data[0];
          if (item) this.editorData = item.content || "";
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    saveData() {
      this.saving = true;
      this.$axios
        .put("superadmin/cms?identifier=refund_policy", {
          content: this.editorData,
        })
        .then((res) => {
          this.saving = false;
          const variant = res.data.status ? "green accent-4" : "red accent-3";
          this.setAlert({
            showAlert: true,
            variant,
            timeout: 3000,
            message: res.data.message,
          });
          if (res.data.status) this.getData();
        })
        .catch(() => {
          this.saving = false;
        });
    },
  },

  mounted() {
    this.getData();
  },
};
</script>

<style scoped>
.ism-cms-wrapper {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

/* ── Card ────────────────────────────────── */
.ism-cms-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* ── Header ──────────────────────────────── */
.ism-cms-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  flex-wrap: wrap;
  background: linear-gradient(
    180deg,
    var(--bg-primary) 0%,
    var(--card-bg) 100%
  );
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}
.ism-cms-header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.ism-cms-icon-wrap {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ism-cms-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text-primary);
  letter-spacing: -0.02em;
  font-family: "Inter", sans-serif;
}
.ism-cms-subtitle {
  font-size: 11.5px;
  color: var(--text-secondary);
  font-family: "Inter", sans-serif;
  margin-top: 1px;
}
.ism-cms-header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* ── Sub Header (Status & Tabs) ──────────── */
.ism-cms-sub-header {
  border-bottom: 1px solid var(--border-color);
  background: var(--card-bg);
  flex-shrink: 0;
}

/* ── Buttons ─────────────────────────────── */
.ism-btn-accent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, var(--accent), var(--accent-hover));
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px var(--accent-glow);
  font-family: "Inter", sans-serif;
  white-space: nowrap;
}
.ism-btn-accent:hover:not(:disabled) {
  filter: brightness(1.1);
  transform: translateY(-1px);
}
.ism-btn-accent:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Mode Tab Bar (Society Detail Style) ─── */
.ism-mode-bar {
  display: flex;
  gap: 8px;
  padding: 0 28px;
  background: var(--card-bg);
}
.ism-mode-btn {
  background: none;
  border: none;
  padding: 14px 24px;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 3px solid transparent;
  white-space: nowrap;
  transition: all 0.2s ease;
  font-family: "Inter", sans-serif;
  cursor: pointer;
}
.ism-mode-btn:hover {
  color: var(--text-primary);
}
.ism-mode-btn.active {
  color: var(--accent);
  border-bottom-color: var(--accent);
}

/* ── Scrollable Content Area ─────────────── */
.ism-cms-scroll-content {
  flex: 1;
  overflow-y: auto;
  background: var(--card-bg);
}

/* ── Editor Pane ─────────────────────────── */
.ism-editor-pane {
  padding: 24px;
}

.ism-editor-skeleton {
  padding: 8px 0;
}
.ism-shimmer {
  background: linear-gradient(
    90deg,
    var(--bg-primary) 25%,
    var(--border-color) 50%,
    var(--bg-primary) 75%
  );
  background-size: 200% 100%;
  animation: ism-shimmer 1.5s infinite linear;
}
@keyframes ism-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── CKEditor Theming ────────────────────── */
.ism-editor-wrap >>> .ck-editor__main .ck-content {
  min-height: 400px;
  height: calc(100vh - 360px);
  font-family: "Inter", sans-serif !important;
  font-size: 14px !important;
  color: var(--text-primary) !important;
  background: var(--card-bg) !important;
  border-color: var(--border-color2) !important;
  border-radius: 0 0 8px 8px !important;
}
.ism-editor-wrap >>> .ck-toolbar {
  background: var(--bg-primary) !important;
  border-color: var(--border-color2) !important;
  border-radius: 8px 8px 0 0 !important;
}
.ism-editor-wrap >>> .ck-button {
  color: var(--text-secondary) !important;
}
.ism-editor-wrap >>> .ck-button:hover {
  background: var(--accent-lt) !important;
  color: var(--accent) !important;
}
.ism-editor-wrap >>> .ck-editor {
  border-radius: 8px !important;
  border: 1px solid var(--border-color2) !important;
  overflow: hidden;
}

/* ── Preview Pane ────────────────────────── */
.ism-preview-pane {
  padding: 24px;
}

/* ── Document render ─────────────────────── */
.ism-preview-doc {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 30px;
  min-height: 400px;
  height: calc(100vh - 320px);
  overflow-y: auto;
  margin: 0 auto;
  box-shadow: var(--shadow-md);
}

/* Rendered HTML content typography */
.ism-preview-content >>> h1,
.ism-preview-content >>> h2,
.ism-preview-content >>> h3,
.ism-preview-content >>> h4 {
  font-family: "Inter", sans-serif !important;
  color: var(--text-primary) !important;
  letter-spacing: -0.02em;
  line-height: 1.3;
  margin: 24px 0 10px;
}
.ism-preview-content >>> h1 {
  font-size: 20px !important;
  font-weight: 800 !important;
}
.ism-preview-content >>> h2 {
  font-size: 17px !important;
  font-weight: 700 !important;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
}
.ism-preview-content >>> h3 {
  font-size: 15px !important;
  font-weight: 700 !important;
}
.ism-preview-content >>> p {
  font-size: 14px !important;
  color: var(--text-secondary) !important;
  font-family: "Inter", sans-serif !important;
  line-height: 1.7 !important;
  margin: 0 0 12px;
}
.ism-preview-content >>> ul,
.ism-preview-content >>> ol {
  padding-left: 20px;
  margin: 0 0 12px;
}
.ism-preview-content >>> li {
  font-size: 14px !important;
  color: var(--text-secondary) !important;
  font-family: "Inter", sans-serif !important;
  line-height: 1.7 !important;
  margin-bottom: 5px;
}
.ism-preview-content >>> a {
  color: var(--accent) !important;
  text-decoration: none;
  font-weight: 600;
}
.ism-preview-content >>> a:hover {
  text-decoration: underline;
}
.ism-preview-content >>> strong {
  color: var(--text-primary) !important;
  font-weight: 700 !important;
}
.ism-preview-content >>> blockquote {
  border-left: 3px solid var(--accent) !important;
  margin: 14px 0 !important;
  padding: 10px 16px !important;
  background: var(--accent-lt) !important;
  border-radius: 0 8px 8px 0 !important;
  color: var(--text-primary) !important;
  font-style: italic;
}
.ism-preview-content >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 14px 0;
}
.ism-preview-content >>> th {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  font-size: 12.5px !important;
  font-weight: 700 !important;
  padding: 8px 12px !important;
  border: 1px solid var(--border-color) !important;
}
.ism-preview-content >>> td {
  font-size: 13px !important;
  color: var(--text-secondary) !important;
  padding: 8px 12px !important;
  border: 1px solid var(--border-color) !important;
}

/* Empty state */
.ism-preview-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}
.ism-preview-empty p {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: "Inter", sans-serif;
  margin: 10px 0 0;
}

@media (max-width: 900px) {
  .ism-mode-bar { padding: 0 20px; }
  .ism-preview-doc { padding: 30px; margin: 0 10px; }
}
@media (max-width: 600px) {
  .ism-cms-wrapper { height: auto; min-height: auto; margin-bottom: 16px; }
  .ism-cms-header { padding: 16px; gap: 12px; }
  .ism-cms-header-left { gap: 10px; }
  .ism-cms-icon-wrap { width: 36px; height: 36px; }
  .ism-cms-title { font-size: 15px; }
  .ism-cms-subtitle { font-size: 11px; }
  .ism-cms-header-actions { width: 100%; }
  .ism-btn-accent { width: 100%; justify-content: center; padding: 10px; }
  
  .ism-mode-btn { padding: 12px 16px; font-size: 13px; }
  .ism-editor-pane, .ism-preview-pane { padding: 12px; }
  
  .ism-editor-wrap >>> .ck-editor__main .ck-content {
    height: auto !important;
    min-height: 400px !important;
  }
  
  .ism-preview-doc { 
    padding: 20px 16px; 
    height: auto !important;
    min-height: 400px !important;
    overflow: visible !important;
  }
  .ism-preview-doc-title { font-size: 20px; }
}
</style>
